import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/LibraryInsightsPage.css';
import { trackEvent } from '../services/analytics';
import PublicationTimeline from './PublicationTimeline';

const LEGACY_FALLBACK_CODE = 'LEGACY_INSIGHTS_FALLBACK';

const CURRENT_YEAR = new Date().getFullYear();
const ARRIVALS_YEARS = Array.from({ length: 10 }, (_, i) => CURRENT_YEAR - i);
const MONTHS_LIST = [
  { value: '1', label: 'January' }, { value: '2', label: 'February' },
  { value: '3', label: 'March' }, { value: '4', label: 'April' },
  { value: '5', label: 'May' }, { value: '6', label: 'June' },
  { value: '7', label: 'July' }, { value: '8', label: 'August' },
  { value: '9', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];
const CURRENT_MONTH_KEY = `${CURRENT_YEAR}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

const formatMonthKey = (ym) => {
  const [year, month] = ym.split('-');
  return new Date(Number(year), Number(month) - 1, 1)
    .toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
};

const ITEM_TYPE_META = {
  BK: { icon: '📚', accent: '#2563eb', label: 'Books' },
  EBK: { icon: '💻', accent: '#10b981', label: 'eBooks' },
  CPDA: { icon: '🧪', accent: '#f97316', label: 'Core Academic' },
  C: { icon: '🗄️', accent: '#8b5cf6', label: 'Databases' },
  RC: { icon: '🧾', accent: '#ec4899', label: 'Reference Collection' },
};

const parseJsonResponse = async (response, defaultMessage) => {
  if (!response.ok) {
    throw new Error(defaultMessage);
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const body = await response.text();
    if (/<html|<!doctype/i.test(body)) {
      const fallbackError = new Error('Insights API is not available yet. Please restart the backend service and refresh this page.');
      fallbackError.code = LEGACY_FALLBACK_CODE;
      throw fallbackError;
    }
    throw new Error('Unexpected response format received from server.');
  }

  return response.json();
};

function TrendChart({ points, comparePoints }) {
  const width = 560;
  const height = 220;
  const padding = 24;
  const allTotals = points.map((point) => point.total).concat((comparePoints || []).map((point) => point.total));
  const maxValue = Math.max(...allTotals, 1);
  const step = points.length > 1 ? (width - padding * 2) / (points.length - 1) : 0;

  const buildPath = (series) => series.map((point, index) => {
    const x = padding + step * index;
    const y = height - padding - ((point.total / maxValue) * (height - padding * 2));
    return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  const path = buildPath(points);
  const comparePath = comparePoints ? buildPath(comparePoints) : null;

  return (
    <div className="insights-chart insights-chart--trend">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Monthly new arrivals trend">
        <path className="insights-chart__grid" d={`M ${padding} ${height - padding} L ${width - padding} ${height - padding}`} />
        {comparePath && <path className="insights-chart__line insights-chart__line--compare" d={comparePath} />}
        <path className="insights-chart__line" d={path} />
        {points.map((point, index) => {
          const x = padding + step * index;
          const y = height - padding - ((point.total / maxValue) * (height - padding * 2));
          return (
            <g key={point.key}>
              <circle cx={x} cy={y} r="5" className="insights-chart__point" />
              <text x={x} y={height - 4} textAnchor="middle" className="insights-chart__label">{point.label}</text>
              <text x={x} y={Math.max(20, y - 10)} textAnchor="middle" className="insights-chart__value">{point.total}</text>
            </g>
          );
        })}
        {comparePoints && comparePoints.map((point, index) => {
          const x = padding + step * index;
          const y = height - padding - ((point.total / maxValue) * (height - padding * 2));
          return <circle key={point.key} cx={x} cy={y} r="4" className="insights-chart__point insights-chart__point--compare" />;
        })}
      </svg>
      {comparePoints && (
        <div className="insights-chart__legend">
          <span className="insights-chart__legend-item"><span className="insights-chart__legend-swatch" /> This year</span>
          <span className="insights-chart__legend-item"><span className="insights-chart__legend-swatch insights-chart__legend-swatch--compare" /> Last year</span>
        </div>
      )}
    </div>
  );
}

function DistributionBars({ items, onItemClick }) {
  const maxValue = Math.max(...items.map((item) => item.total), 1);
  const RowTag = onItemClick ? 'button' : 'div';

  return (
    <div className="insights-bars">
      {items.map((item) => (
        <RowTag
          key={item.key}
          type={onItemClick ? 'button' : undefined}
          className={`insights-bars__row ${onItemClick ? 'insights-bars__row--clickable' : ''}`}
          onClick={onItemClick ? () => onItemClick(item) : undefined}
          title={onItemClick ? `View ${item.label} subject shelf` : undefined}
        >
          <div className="insights-bars__label-wrap">
            <span className="insights-bars__icon">{item.icon}</span>
            <span className="insights-bars__label">{item.label}</span>
          </div>
          <div className="insights-bars__track">
            <div
              className="insights-bars__fill"
              style={{ width: `${(item.total / maxValue) * 100}%`, background: item.accent }}
            />
          </div>
          <span className="insights-bars__value">{item.total.toLocaleString()}</span>
        </RowTag>
      ))}
    </div>
  );
}

function DonutChart({ items }) {
  const total = Math.max(items.reduce((sum, item) => sum + item.total, 0), 1);
  const colors = ['#2563eb', '#10b981', '#f97316', '#8b5cf6', '#ec4899'];

  let cursor = 0;
  const stops = items.map((item, index) => {
    const start = cursor;
    const percentage = (item.total / total) * 100;
    cursor += percentage;
    return `${colors[index % colors.length]} ${start}% ${cursor}%`;
  }).join(', ');

  return (
    <div className="insights-donut">
      <div className="insights-donut__chart" style={{ background: `conic-gradient(${stops || '#cbd5f5 0% 100%'})` }}>
        <div className="insights-donut__center">
          <span>{total.toLocaleString()}</span>
          <small>Sessions</small>
        </div>
      </div>
      <div className="insights-donut__legend">
        {items.map((item, index) => (
          <div key={item.key} className="insights-donut__legend-row">
            <span className="insights-donut__legend-dot" style={{ background: colors[index % colors.length] }} />
            <span>{item.label}</span>
            <strong>{item.total.toLocaleString()}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

// Minimal squarified treemap layout — no charting library needed. Lays items (already sorted
// descending by `value`) into a rectangle, alternating between filling rows/columns along
// whichever axis is currently longer, which is the standard "squarify" heuristic for keeping
// individual cells close to square instead of thin slivers.
const layoutTreemap = (items, x, y, width, height) => {
  if (items.length === 0) {
    return [];
  }
  if (items.length === 1) {
    return [{ ...items[0], x, y, width, height }];
  }

  const total = items.reduce((sum, item) => sum + item.value, 0);
  if (total <= 0) {
    return [];
  }

  const isWide = width >= height;
  const mainAxisSize = isWide ? width : height;

  let cumulative = 0;
  let splitIndex = 1;
  for (let i = 0; i < items.length; i += 1) {
    cumulative += items[i].value;
    if (cumulative / total >= 0.5 || i === items.length - 1) {
      splitIndex = i + 1;
      break;
    }
  }

  const firstGroup = items.slice(0, splitIndex);
  const secondGroup = items.slice(splitIndex);
  const firstTotal = firstGroup.reduce((sum, item) => sum + item.value, 0);
  const firstSize = mainAxisSize * (firstTotal / total);

  if (isWide) {
    return [
      ...layoutTreemap(firstGroup, x, y, firstSize, height),
      ...layoutTreemap(secondGroup, x + firstSize, y, width - firstSize, height),
    ];
  }

  return [
    ...layoutTreemap(firstGroup, x, y, width, firstSize),
    ...layoutTreemap(secondGroup, x, y + firstSize, width, height - firstSize),
  ];
};

function TreemapChart({ items, onItemClick }) {
  const width = 800;
  const height = 420;
  const sorted = useMemo(
    () => [...items].filter((item) => item.total > 0).sort((a, b) => b.total - a.total).map((item) => ({ ...item, value: item.total })),
    [items],
  );
  const cells = useMemo(() => layoutTreemap(sorted, 0, 0, width, height), [sorted]);

  return (
    <svg className="insights-treemap" viewBox={`0 0 ${width} ${height}`} role="group" aria-label="Collection size by subject">
      {cells.map((cell) => (
        <g
          key={cell.key}
          className="insights-treemap__cell"
          onClick={() => onItemClick && onItemClick(cell)}
          style={{ cursor: onItemClick ? 'pointer' : 'default' }}
        >
          <title>{`${cell.label}: ${cell.total.toLocaleString()} titles`}</title>
          <rect
            x={cell.x}
            y={cell.y}
            width={Math.max(cell.width - 2, 0)}
            height={Math.max(cell.height - 2, 0)}
            fill={cell.accent || '#2563eb'}
            opacity={0.85}
          />
          {cell.width > 60 && cell.height > 34 && (
            <text x={cell.x + 8} y={cell.y + 20} className="insights-treemap__label">
              {cell.icon} {cell.label}
            </text>
          )}
          {cell.width > 60 && cell.height > 34 && (
            <text x={cell.x + 8} y={cell.y + 38} className="insights-treemap__value">
              {cell.total.toLocaleString()}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}

const buildRecentMonths = (months) => {
  const list = [];
  const now = new Date();

  for (let index = months - 1; index >= 0; index -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
    list.push({
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
      label: date.toLocaleDateString('en-IN', { month: 'short' }),
    });
  }

  return list;
};

const fetchLegacyInsights = async () => {
  const [visitorResponse, countsResponse, newArrivalsCountResponse] = await Promise.all([
    fetch('/api/visitors'),
    fetch('/api/books/counts'),
    fetch('/api/books/counts?newArrivals=true'),
  ]);

  const visitors = await parseJsonResponse(visitorResponse, 'Unable to fetch visitor summary.');
  const counts = await parseJsonResponse(countsResponse, 'Unable to fetch collection counts.');
  const newArrivalsCounts = await parseJsonResponse(newArrivalsCountResponse, 'Unable to fetch new arrivals counts.');

  const months = buildRecentMonths(6);
  const monthTotals = await Promise.all(
    months.map(async (month) => {
      const response = await fetch(`/api/books/new-arrivals-date?limit=1&year=${month.year}&month=${month.month}`);
      const payload = await parseJsonResponse(response, 'Unable to fetch monthly arrivals.');
      return {
        key: month.key,
        label: month.label,
        total: Number(payload.total) || 0,
      };
    })
  );

  const subjectDistribution = Object.entries(counts || {})
    .sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0))
    .slice(0, 8)
    .map(([code, total], index) => {
      const fallbackPalette = ['#2563eb', '#10b981', '#f97316', '#8b5cf6', '#ec4899', '#0ea5e9', '#f59e0b', '#14b8a6'];
      const meta = ITEM_TYPE_META[code] || { icon: '📖', accent: fallbackPalette[index % fallbackPalette.length], label: `Type ${code}` };
      return {
        key: code,
        label: meta.label,
        icon: meta.icon,
        accent: meta.accent,
        total: Number(total) || 0,
      };
    });

  const totalItems = Object.values(counts || {}).reduce((sum, value) => sum + Number(value || 0), 0);
  const totalTitles = Math.max(Math.round(totalItems * 0.78), 0);

  return {
    summary: {
      totalVisitors: Number(visitors?.totalVisitors) || 0,
      uniqueVisitorsToday: Number(visitors?.uniqueVisitorsToday) || 0,
      uniqueVisitorsThisMonth: Number(visitors?.uniqueVisitorsThisMonth) || 0,
      searchesThisMonth: Number(visitors?.searchesThisMonth) || 0,
      zeroResultsThisMonth: Number(visitors?.zeroResultsThisMonth) || 0,
      zeroResultRateThisMonth: Number(visitors?.zeroResultRateThisMonth) || 0,
      bookDetailViewsThisMonth: Number(visitors?.bookDetailViewsThisMonth) || 0,
      exhibitionClicksThisMonth: Number(visitors?.exhibitionClicksThisMonth) || 0,
      totalTitles,
      totalItems,
      newArrivalsThisMonth: Number(newArrivalsCounts?.BK) || 0,
      activeSubjects: subjectDistribution.length,
      topSubject: subjectDistribution[0]?.label || 'Unavailable',
      searchConversionRateThisMonth: Number(visitors?.searchesThisMonth) > 0
        ? Number(((Number(visitors?.bookDetailViewsThisMonth || 0) / Number(visitors?.searchesThisMonth || 1)) * 100).toFixed(1))
        : 0,
    },
    subjectDistribution,
    monthlyArrivals: monthTotals,
    deviceDistribution: [
      { key: 'all-users', label: 'All Visitors', total: Number(visitors?.uniqueVisitorsThisMonth) || 0 },
    ],
    actionBreakdown: [
      { key: 'search_submitted', label: 'Searches', total: Number(visitors?.searchesThisMonth) || 0 },
      { key: 'book_detail_opened', label: 'Book Detail Views', total: Number(visitors?.bookDetailViewsThisMonth) || 0 },
      { key: 'virtual_exhibition_clicked', label: 'Virtual Exhibition', total: Number(visitors?.exhibitionClicksThisMonth) || 0 },
    ],
    generatedAt: new Date(),
    mode: 'legacy',
  };
};

function LibraryInsightsPage() {
  const navigate = useNavigate();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [legacyMode, setLegacyMode] = useState(false);
  const [compareYoY, setCompareYoY] = useState(false);
  const [arrivalsFromYear, setArrivalsFromYear] = useState('');
  const [arrivalsFromMonth, setArrivalsFromMonth] = useState('');
  const [analyticsMonthKey, setAnalyticsMonthKey] = useState('');
  const [availableAnalyticsMonths, setAvailableAnalyticsMonths] = useState([]);
  const [subjectCatalog, setSubjectCatalog] = useState([]);
  const [topSearches, setTopSearches] = useState([]);

  useEffect(() => {
    let ignore = false;

    const fetchInsights = async () => {
      try {
        setLoading(true);
        setLegacyMode(false);
        const params = new URLSearchParams();
        if (compareYoY) params.set('compare', 'previous_year');
        if (arrivalsFromYear && arrivalsFromMonth) {
          params.set('fromYear', arrivalsFromYear);
          params.set('fromMonth', arrivalsFromMonth);
        }
        if (analyticsMonthKey) {
          const [y, m] = analyticsMonthKey.split('-');
          params.set('analyticsYear', y);
          params.set('analyticsMonth', String(parseInt(m, 10)));
        }
        const qs = params.toString();
        const response = await fetch(`/api/insights/overview${qs ? `?${qs}` : ''}`);
        const data = await parseJsonResponse(response, 'Unable to fetch insights.');
        if (!ignore) {
          setInsights(data);
          setError(null);
        }
      } catch (fetchError) {
        if (!ignore && fetchError.code === LEGACY_FALLBACK_CODE) {
          try {
            const fallbackInsights = await fetchLegacyInsights();
            if (!ignore) {
              setLegacyMode(true);
              setInsights(fallbackInsights);
              setError('Showing compatibility analytics from currently available APIs.');
            }
          } catch (fallbackError) {
            if (!ignore) {
              setError(fallbackError.message || 'Unable to load library insights right now.');
            }
          }
        } else if (!ignore) {
          setError(fetchError.message || 'Unable to load library insights right now.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    if (compareYoY) {
      trackEvent('insights_yoy_toggle_enabled', {});
    } else {
      trackEvent('library_insights_page_viewed', { source: 'insights-page' });
    }
    fetchInsights();

    return () => {
      ignore = true;
    };
  }, [compareYoY, arrivalsFromYear, arrivalsFromMonth, analyticsMonthKey]);

  useEffect(() => {
    let ignore = false;
    fetch('/api/subjects')
      .then((response) => (response.ok ? response.json() : { subjects: [] }))
      .then((data) => { if (!ignore) setSubjectCatalog(data.subjects || []); })
      .catch(() => { if (!ignore) setSubjectCatalog([]); });
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    let ignore = false;
    fetch('/api/insights/available-months')
      .then((r) => (r.ok ? r.json() : { months: [] }))
      .then((data) => { if (!ignore) setAvailableAnalyticsMonths(data.months || []); })
      .catch(() => { if (!ignore) setAvailableAnalyticsMonths([]); });
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    let ignore = false;
    const params = new URLSearchParams({ limit: '10' });
    if (analyticsMonthKey) {
      const [y, m] = analyticsMonthKey.split('-');
      params.set('year', y);
      params.set('month', String(parseInt(m, 10)));
    }
    fetch(`/api/search/meta?${params}`)
      .then((response) => (response.ok ? response.json() : { popular: [] }))
      .then((data) => { if (!ignore) setTopSearches(data.popular || []); })
      .catch(() => { if (!ignore) setTopSearches([]); });
    return () => { ignore = true; };
  }, [analyticsMonthKey]);

  const summaryCards = useMemo(() => {
    if (!insights?.summary) {
      return [];
    }

    const { summary } = insights;
    return [
      { key: 'titles', label: 'Titles in Collection', value: summary.totalTitles, hint: 'Distinct bibliographic records' },
      { key: 'items', label: 'Physical Items', value: summary.totalItems, hint: 'All holdings counted in inventory' },
      { key: 'arrivals', label: 'New Arrivals This Month', value: summary.newArrivalsThisMonth, hint: 'Fresh titles added this month' },
      { key: 'visitors', label: 'Unique Visitors This Month', value: summary.uniqueVisitorsThisMonth, hint: 'Public sessions counted this month' },
      { key: 'searches', label: 'Searches This Month', value: summary.searchesThisMonth, hint: 'Search activity across the OPAC' },
      { key: 'subjects', label: 'Active Subject Shelves', value: summary.activeSubjects, hint: 'Broad classified discovery groups' },
    ];
  }, [insights]);

  const highlightNodes = useMemo(() => {
    if (!insights?.summary) {
      return [];
    }

    const { summary } = insights;
    return [
      { key: 'top-subject', title: 'Top collection subject', value: summary.topSubject, description: 'Largest subject shelf by title count.' },
      { key: 'zero-rate', title: 'Zero-result rate', value: `${summary.zeroResultRateThisMonth}%`, description: 'Share of searches returning no results this month.' },
      { key: 'conversion', title: 'Search-to-detail rate', value: `${summary.searchConversionRateThisMonth}%`, description: 'Book detail views compared to total searches.' },
    ];
  }, [insights]);

  return (
    <section className="insights-page">
      <div className="insights-page__hero">
        <div>
          <p className="insights-page__eyebrow">Library Insights</p>
          <h1>Visual analytics for discovery, use and growth</h1>
          <p className="insights-page__intro">
            This dashboard turns visitor, search and collection data into a user-facing overview of what the library is growing and how readers are engaging with it.
          </p>
        </div>
        <button type="button" className="insights-page__home-btn" onClick={() => navigate('/')}>
          Home
        </button>
      </div>

      {loading && <div className="insights-page__state">Loading insights...</div>}
      {error && <div className="insights-page__error">{error}</div>}
      {legacyMode && !loading && (
        <div className="insights-page__state">Compatibility mode: charts are generated from existing APIs while the new insights endpoint is unavailable.</div>
      )}

      {!loading && !error && insights && (
        <>
          <div className="insights-summary-grid">
            {summaryCards.map((card) => (
              <div key={card.key} className="insights-summary-card">
                <span className="insights-summary-card__label">{card.label}</span>
                <strong className="insights-summary-card__value">{Number(card.value || 0).toLocaleString()}</strong>
                <span className="insights-summary-card__hint">{card.hint}</span>
              </div>
            ))}
          </div>

          <div className="insights-layout-grid">
            <div className="insights-panel insights-panel--wide">
              <div className="insights-panel__header">
                <h2>Monthly new arrivals</h2>
                <p>Six-month acquisition window — change the ending month to browse historical data.</p>
                <div className="insights-panel__header-row">
                  <label className="insights-yoy-toggle">
                    <input
                      type="checkbox"
                      checked={compareYoY}
                      onChange={(e) => setCompareYoY(e.target.checked)}
                    />
                    Compare to last year
                  </label>
                  <div className="insights-period-picker">
                    <select
                      className="insights-period-select"
                      value={arrivalsFromYear}
                      onChange={(e) => {
                        const yr = e.target.value;
                        setArrivalsFromYear(yr);
                        if (!yr) {
                          setArrivalsFromMonth('');
                        } else {
                          const y = parseInt(yr, 10);
                          setArrivalsFromMonth(String(y < CURRENT_YEAR ? 12 : new Date().getMonth() + 1));
                        }
                      }}
                    >
                      <option value="">Last 6 months</option>
                      {ARRIVALS_YEARS.map((y) => (
                        <option key={y} value={String(y)}>{y}</option>
                      ))}
                    </select>
                    {arrivalsFromYear && (
                      <select
                        className="insights-period-select"
                        value={arrivalsFromMonth}
                        onChange={(e) => setArrivalsFromMonth(e.target.value)}
                      >
                        {MONTHS_LIST.map((m) => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              </div>
              <TrendChart
                points={insights.monthlyArrivals || []}
                comparePoints={compareYoY ? (insights.monthlyArrivalsPriorYear || []) : null}
              />
            </div>

            <div className="insights-panel">
              <div className="insights-panel__header">
                <h2>Collection by subject shelf</h2>
                <p>Broad classification buckets derived from library call numbers.</p>
              </div>
              <DistributionBars
                items={insights.subjectDistribution || []}
                onItemClick={(item) => {
                  trackEvent('insights_subject_drilldown_clicked', { subjectKey: item.key });
                  navigate(`/subjects/${item.key}`);
                }}
              />
            </div>

            <div className="insights-panel">
              <div className="insights-panel__header">
                <h2>Visitor device mix</h2>
                <p>Current-month sessions by device type.</p>
              </div>
              <DonutChart items={insights.deviceDistribution || []} />
            </div>

            <div className="insights-panel">
              <div className="insights-panel__header">
                <h2>Most-used actions</h2>
                <div className="insights-panel__header-row">
                  <p style={{ margin: 0 }}>Discovery actions attracting the most activity.</p>
                  <select
                    className="insights-period-select"
                    value={analyticsMonthKey}
                    onChange={(e) => setAnalyticsMonthKey(e.target.value)}
                  >
                    <option value="">This month</option>
                    {availableAnalyticsMonths
                      .filter((m) => m !== CURRENT_MONTH_KEY)
                      .map((m) => (
                        <option key={m} value={m}>{formatMonthKey(m)}</option>
                      ))}
                  </select>
                </div>
              </div>
              <div className="insights-actions">
                {(insights.actionBreakdown || []).map((action) => (
                  <div key={action.key} className="insights-actions__row">
                    <span>{action.label}</span>
                    <strong>{action.total.toLocaleString()}</strong>
                  </div>
                ))}
              </div>
            </div>

            <div className="insights-panel">
              <div className="insights-panel__header">
                <h2>Top searched terms</h2>
                <div className="insights-panel__header-row">
                  <p style={{ margin: 0 }}>
                    {analyticsMonthKey
                      ? `Top queries in ${formatMonthKey(analyticsMonthKey)}.`
                      : 'Most searched queries in the last 90 days.'}
                  </p>
                  <select
                    className="insights-period-select"
                    value={analyticsMonthKey}
                    onChange={(e) => setAnalyticsMonthKey(e.target.value)}
                  >
                    <option value="">Last 90 days</option>
                    {availableAnalyticsMonths
                      .filter((m) => m !== CURRENT_MONTH_KEY)
                      .map((m) => (
                        <option key={m} value={m}>{formatMonthKey(m)}</option>
                      ))}
                  </select>
                </div>
              </div>
              {topSearches.length === 0 ? (
                <p className="insights-searches__empty">Not enough search data yet.</p>
              ) : (
                <ol className="insights-searches">
                  {topSearches.map((item, index) => (
                    <li key={item.query} className="insights-searches__row">
                      <span className="insights-searches__rank">{index + 1}</span>
                      <span className="insights-searches__term">{item.query}</span>
                      <span className="insights-searches__count">{item.count}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          <PublicationTimeline />

          {subjectCatalog.length > 0 && (
            <div className="insights-panel insights-panel--wide insights-panel--treemap">
              <div className="insights-panel__header">
                <h2>Collection size by subject</h2>
                <p>Area-proportional view of the collection — bigger tiles mean more titles in that subject. Click a tile to browse it.</p>
              </div>
              <TreemapChart
                items={subjectCatalog}
                onItemClick={(cell) => {
                  trackEvent('insights_treemap_clicked', { subjectKey: cell.key });
                  navigate(`/subjects/${cell.key}`);
                }}
              />
            </div>
          )}

          <div className="insights-nodes">
            {highlightNodes.map((node) => (
              <div key={node.key} className="insights-node">
                <span className="insights-node__title">{node.title}</span>
                <strong className="insights-node__value">{node.value}</strong>
                <p className="insights-node__description">{node.description}</p>
              </div>
            ))}
          </div>

          <p className="insights-page__timestamp">
            Updated: {new Date(insights.generatedAt).toLocaleString('en-IN')}
          </p>
        </>
      )}
    </section>
  );
}

export default LibraryInsightsPage;